import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { HelpCircle, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { LETTER_TOUR_STEPS, LetterTourStep } from './letterTourSteps';

interface LetterTourProps {
    open: boolean;
    onClose: () => void;
    onStepChange?: (step: LetterTourStep) => void;
}

type Hole = { top: number; left: number; width: number; height: number };

const PAD = 8;

function pickVisibleTarget(selector?: string): HTMLElement | null {
    if (!selector) return null;
    const nodes = Array.from(document.querySelectorAll(selector)) as HTMLElement[];
    for (const el of nodes) {
        const r = el.getBoundingClientRect();
        const style = window.getComputedStyle(el);
        if (style.display === 'none' || style.visibility === 'hidden') continue;
        if (r.width < 4 || r.height < 4) continue;
        return el;
    }
    return nodes[0] || null;
}

function measureTarget(selector?: string): Hole | null {
    const el = pickVisibleTarget(selector);
    if (!el) return null;
    const r = el.getBoundingClientRect();
    if (r.width < 4 || r.height < 4) return null;
    return {
        top: Math.max(0, r.top - PAD),
        left: Math.max(0, r.left - PAD),
        width: Math.min(window.innerWidth - Math.max(0, r.left - PAD), r.width + PAD * 2),
        height: Math.min(window.innerHeight - Math.max(0, r.top - PAD), r.height + PAD * 2),
    };
}

function scrollTargetIntoView(selector?: string): void {
    const el = pickVisibleTarget(selector);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
}

const LetterTour: React.FC<LetterTourProps> = ({ open, onClose, onStepChange }) => {
    const [index, setIndex] = useState(0);
    const [hole, setHole] = useState<Hole | null>(null);
    const [cardPos, setCardPos] = useState<{ top: number; left: number }>({ top: 80, left: 24 });
    const timersRef = useRef<number[]>([]);

    const step = LETTER_TOUR_STEPS[index];
    const total = LETTER_TOUR_STEPS.length;
    const isFirst = index === 0;
    const isLast = index >= total - 1;

    const clearTimers = () => {
        timersRef.current.forEach((id) => window.clearTimeout(id));
        timersRef.current = [];
    };

    const placeCard = useCallback((h: Hole | null) => {
        const cardW = Math.min(360, window.innerWidth - 32);
        const cardH = 260;
        if (!h) {
            setCardPos({
                top: Math.max(24, (window.innerHeight - cardH) / 2),
                left: Math.max(16, (window.innerWidth - cardW) / 2),
            });
            return;
        }

        const spaceBelow = window.innerHeight - (h.top + h.height);
        const placeBelow = spaceBelow > cardH + 24 || h.top < cardH + 24;
        let top = placeBelow ? h.top + h.height + 12 : h.top - cardH - 12;
        let left = h.left + h.width / 2 - cardW / 2;
        top = Math.min(Math.max(12, top), window.innerHeight - cardH - 12);
        left = Math.min(Math.max(16, left), window.innerWidth - cardW - 16);
        setCardPos({ top, left });
    }, []);

    const refreshLayout = useCallback(() => {
        const current = LETTER_TOUR_STEPS[index];
        if (!current) return;
        clearTimers();
        const apply = () => {
            const h = measureTarget(current.target);
            setHole(h);
            placeCard(h);
        };
        scrollTargetIntoView(current.target);
        timersRef.current.push(window.setTimeout(apply, 80));
        timersRef.current.push(window.setTimeout(apply, 280));
        timersRef.current.push(window.setTimeout(apply, 520));
        timersRef.current.push(window.setTimeout(apply, 800));
    }, [index, placeCard]);

    useEffect(() => {
        if (!open) return;
        setIndex(0);
    }, [open]);

    useEffect(() => {
        if (!open || !step) return;
        onStepChange?.(step);
        const t = window.setTimeout(() => refreshLayout(), 60);
        return () => window.clearTimeout(t);
    }, [open, step, onStepChange, refreshLayout]);

    useLayoutEffect(() => {
        if (!open) return;
        const onResize = () => refreshLayout();
        const onScroll = () => {
            const current = LETTER_TOUR_STEPS[index];
            if (!current) return;
            const h = measureTarget(current.target);
            setHole(h);
            placeCard(h);
        };
        window.addEventListener('resize', onResize);
        window.addEventListener('scroll', onScroll, true);
        return () => {
            window.removeEventListener('resize', onResize);
            window.removeEventListener('scroll', onScroll, true);
            clearTimers();
        };
    }, [open, refreshLayout, index, placeCard]);

    if (!open || !step) return null;

    const goNext = () => {
        if (isLast) onClose();
        else setIndex((i) => i + 1);
    };

    const goPrev = () => {
        if (!isFirst) setIndex((i) => i - 1);
    };

    const cardW = Math.min(360, window.innerWidth - 32);

    return createPortal(
        <div className="fixed inset-0 z-[100] no-print" role="dialog" aria-modal="true" aria-labelledby="letter-tour-title">
            <div className="absolute inset-0 pointer-events-auto" onClick={onClose}>
                {hole ? (
                    <>
                        <div className="absolute inset-0 bg-slate-900/50" />
                        <div
                            className="absolute rounded-xl ring-2 ring-teal-400 ring-offset-2 ring-offset-transparent shadow-[0_0_0_9999px_rgba(15,23,42,0.55)] bg-transparent pointer-events-none transition-all duration-300"
                            style={{
                                top: hole.top,
                                left: hole.left,
                                width: hole.width,
                                height: hole.height,
                            }}
                        />
                    </>
                ) : (
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-[1px]" />
                )}
            </div>

            <div
                className="absolute z-[101] pointer-events-auto"
                style={{ top: cardPos.top, left: cardPos.left, width: cardW }}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="rounded-2xl bg-white border border-slate-200 shadow-2xl overflow-hidden">
                    <div className="px-4 pt-4 pb-2 flex items-start justify-between gap-3">
                        <div className="flex items-center gap-2 min-w-0">
                            <span className="shrink-0 w-8 h-8 rounded-lg bg-teal-50 text-teal-700 flex items-center justify-center">
                                <HelpCircle className="w-4 h-4" />
                            </span>
                            <div className="min-w-0">
                                <p className="text-[10px] font-bold uppercase tracking-wider text-teal-600">
                                    Guide lettre · {index + 1}/{total}
                                </p>
                                <h2 id="letter-tour-title" className="text-base font-bold text-slate-900 leading-snug">
                                    {step.title}
                                </h2>
                            </div>
                        </div>
                        <button
                            type="button"
                            aria-label="Fermer le guide"
                            onClick={onClose}
                            className="shrink-0 p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>

                    <div className="px-4 pb-3">
                        <p className="text-sm text-slate-600 leading-relaxed">{step.body}</p>
                        <div className="mt-3 h-1 rounded-full bg-slate-100 overflow-hidden">
                            <div
                                className="h-full bg-teal-600 rounded-full transition-all duration-300"
                                style={{ width: `${((index + 1) / total) * 100}%` }}
                            />
                        </div>
                    </div>

                    <div className="px-4 py-3 bg-slate-50 border-t border-slate-100 flex items-center justify-between gap-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="text-xs font-semibold text-slate-500 hover:text-slate-800 px-2 py-1.5"
                        >
                            Passer
                        </button>
                        <div className="flex items-center gap-1.5">
                            <button
                                type="button"
                                disabled={isFirst}
                                onClick={goPrev}
                                className="h-9 px-3 rounded-xl border border-slate-200 bg-white text-slate-700 text-sm font-semibold inline-flex items-center gap-1 disabled:opacity-40 hover:bg-slate-50"
                            >
                                <ChevronLeft className="w-4 h-4" />
                                <span className="hidden sm:inline">Retour</span>
                            </button>
                            <button
                                type="button"
                                onClick={goNext}
                                className="h-9 px-3 rounded-xl bg-teal-700 hover:bg-teal-800 text-white text-sm font-semibold inline-flex items-center gap-1"
                            >
                                {isLast ? 'Terminer' : 'Suivant'}
                                {!isLast && <ChevronRight className="w-4 h-4" />}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default LetterTour;
